import { NumberComparisonCondition } from './conditionEnums';

/**
 * Extracts the numeric segment at the end of a condition string after a known prefix.
 * Returns NaN if the prefix is absent or no numeric segment is found.
 */
const getNumberFromCondition = (fieldValue: string, fieldNumberPrefix: string): number => {
	if (!fieldValue.startsWith(fieldNumberPrefix)) {
		return NaN;
	}

	const afterPrefix = fieldValue.substring(fieldNumberPrefix.length);
	if (!afterPrefix) {
		return NaN;
	}

	const lastUnderscoreIndex = afterPrefix.lastIndexOf('_');
	const numPart = afterPrefix.substring(lastUnderscoreIndex + 1);

	return +numPart;
};

/**
 * Checks whether a numeric condition (e.g. equals_5, greater_than_10) holds true for the given number.
 * Returns false for invalid numbers or unrecognized patterns.
 */
const validateNumberCondition = (fieldValue: string, storeItem: number): boolean => {
	if (isNaN(storeItem)) return false;

	// Get all items from the enum
	const keys = Object.keys(NumberComparisonCondition);

	return keys.some((conditionKey) => {
		const condition = NumberComparisonCondition[conditionKey as keyof typeof NumberComparisonCondition];

		if (!condition || !fieldValue.startsWith(condition)) {
			return false;
		}

		if (condition === NumberComparisonCondition.IsNumber) {
			return fieldValue === condition;
		}

		const theNumber = getNumberFromCondition(fieldValue, condition);

		if (isNaN(theNumber)) return false;

		switch (condition) {
			case NumberComparisonCondition.Equals:
				return storeItem == theNumber;
			case NumberComparisonCondition.LessThan:
				return storeItem < theNumber;
			case NumberComparisonCondition.LessThanOrEquals:
				return storeItem <= theNumber;
			case NumberComparisonCondition.GreaterThan:
				return storeItem > theNumber;
			case NumberComparisonCondition.GreaterThanOrEquals:
				return storeItem >= theNumber;
			default:
				return false;
		}
	});
};

/* validating fieldValue in case is_empty, is_number and if fieldValue matches the one in the store.
   will return whatever the rule expect, isPositiveCondition is what the rule result is set in the cms.
*/
export const validateConditions = (storeItem: string, fieldValue: string, isPositiveCondition: boolean): boolean => {
	if (fieldValue == 'is_empty' && !storeItem) return isPositiveCondition;

	if (fieldValue.startsWith('is_number')) {
		const isNumberCondition = validateNumberCondition(fieldValue, +storeItem);

		// if its not negative then its positive
		return isNumberCondition && isPositiveCondition;
	}

	// if fieldValue not equal to the storeItem
	if (!isPositiveCondition && fieldValue != storeItem) return true;

	// if the fieldValue is equal to the storeItem then return true
	return fieldValue == storeItem && isPositiveCondition;
};
